import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, Heart, Shield, Users, Award, ExternalLink, Linkedin } from 'lucide-react';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const AboutUs = () => {
  return (
    <div className="about-us-page overflow-x-hidden">
      <SEO 
        title="About Us"
        description="Meet Paulette Nelson, your trusted independent insurance broker. With years of experience helping Michigan seniors navigate Medicare and health insurance. Personalized service, year-round support."
        keywords="about LifeLine Health Plans, Paulette Nelson, Michigan insurance broker, independent insurance agent, Medicare specialist Michigan, health insurance broker"
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/images/paulette-profile.png" 
                alt="Paulette Abdullah" 
                className="w-full max-w-96 h-auto aspect-square rounded-full mx-auto object-cover shadow-2xl border-8 border-teal-600"
              />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">About LifeLine Health Plans</h1>
              <p className="text-2xl text-gray-800 mb-6">
                Paulette Abdullah | Licensed Insurance Agent
              </p>
              <p className="text-xl text-gray-700 italic mb-8">
                "Retired Medicare Recovery Specialist"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-4xl font-bold mb-8 text-gray-900">Our Mission</h2>
          <Card className="border-4 border-teal-300 bg-teal-50">
            <CardContent className="p-12">
              <p className="text-2xl text-gray-800 leading-relaxed">
                At LifeLine Health Plans, we believe in treating every client like family. Our mission is to provide 
                personalized, honest guidance through the complex world of health insurance—with no pressure, 
                just genuine care and advocacy.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* A Licensed Advisor and Advocate */}
      <section className="py-16 px-4 bg-gray-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">A Licensed Advisor and Advocate</h2>
          
          <div className="space-y-8">
            <Card className="border-2 border-teal-200">
              <CardContent className="p-8">
                <p className="text-xl text-gray-800 leading-relaxed mb-6">
                  For 25 years, Paulette worked with the United States Department of Justice assisting in the recovery of funds lost to Medicare fraud. This duty focused on protecting Medicare from fraud and preserving it for future generations.
                </p>
                <p className="text-xl text-gray-800 leading-relaxed">
                  Today, she serves as a licensed health insurance advisor, providing personalized guidance and education for Medicare, along offering individual, family, dental, and group health insurance coverage.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">TRUSTED GUIDANCE</h3>
                <p className="text-xl text-gray-800 leading-relaxed mb-6">
                  Her approach is straightforward and client-focused—grounded in experience, not sales pressure. Each recommendation is carefully considered to ensure it aligns with the client's needs, budget, and long-term goals.
                </p>
                <p className="text-xl text-gray-800 leading-relaxed">
                  She acts as an advocate for her clients, helping them understand their options, avoid costly mistakes, and make confident decisions in a system that is often complex and overwhelming.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">What Sets Us Apart</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-4 border-teal-300 hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <Shield className="w-16 h-16 text-teal-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Medicare Guidance</h3>
                <p className="text-xl text-gray-800">
                  Clear, step-by-step guidance and education to help clients understand Medicare and confidently select the right coverage.
                </p>
              </CardContent>
            </Card>

            <Card className="border-4 border-teal-300 hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <Users className="w-16 h-16 text-teal-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Individual & Family Coverage</h3>
                <p className="text-xl text-gray-800">
                  Personalized guidance for individuals and families, helping balance coverage, cost, and care.
                </p>
              </CardContent>
            </Card>

            <Card className="border-4 border-teal-300 hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <Heart className="w-16 h-16 text-teal-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Advocacy</h3>
                <p className="text-xl text-gray-800">
                  Guidance centered on your best interests, with clarity and integrity.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Medicare Fraud Education - Repeated Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-white rounded-lg shadow-2xl p-12 border-4 border-red-400">
            <div className="flex items-center justify-center gap-4 mb-6">
              <Shield className="w-16 h-16 text-red-600" />
              <h2 className="text-4xl font-bold text-gray-900">Protect Yourself from Medicare Fraud</h2>
            </div>
            
            <p className="text-2xl text-center text-gray-800 mb-8">
              As a former Medicare fraud investigator, Paulette offers FREE educational seminars on 
              how to protect yourself from scams and fraud.
            </p>
            
            <div className="text-center space-y-4">
              <Button asChild size="lg" className="bg-red-600 hover:bg-red-700 text-white text-2xl px-12 py-8">
                <a href="/medicare-calls">
                  Learn More About Medicare Fraud <ExternalLink className="ml-2" />
                </a>
              </Button>
              <p className="text-lg text-gray-700">
                Schedule a seminar for your senior center, community group, or organization
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <ContactForm />
        </div>
      </section>

      {/* Connect With Us */}
      <section className="py-16 px-4 bg-gray-100">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-4xl font-bold mb-8 text-gray-900">Connect With Us</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Phone className="w-16 h-16 mx-auto mb-4 text-teal-600" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Call Us</h3>
                <a href="tel:734.209.6712" className="text-2xl font-bold text-teal-600 hover:text-teal-700">
                  734.209.6712
                </a>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Mail className="w-16 h-16 mx-auto mb-4 text-teal-600" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Email Us</h3>
                <a href="mailto:Paulette@llplans.com" className="text-xl font-semibold text-teal-600 hover:text-teal-700">
                  Paulette@llplans.com
                </a>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Linkedin className="w-16 h-16 mx-auto mb-4 text-teal-600" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">LinkedIn</h3>
                <Button asChild variant="link" className="text-xl text-teal-600">
                  <a href="https://www.linkedin.com/in/llplans/" target="_blank" rel="noopener noreferrer">
                    Connect with Paulette
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-teal-300">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Office Location</h3>
            <p className="text-2xl text-gray-800">
              28003 Center Oak Court, Ste. 103<br/>
              Wixom, MI 48393
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-teal-50 to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">Ready to Get Started?</h2>
          <p className="text-2xl text-gray-800 mb-8">
            Let's find the perfect health insurance plan for you
          </p>
          <div className="space-y-4">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-2xl px-16 py-8">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Take the Free Survey
              </a>
            </Button>
            <p className="text-lg text-gray-700">
              Or call us directly at <a href="tel:734.209.6712" className="font-bold text-teal-600 hover:text-teal-700">734.209.6712</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
